/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ConfigRevisionResponse, ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UploadConfigRevisionMutationRequest = {
  configId: string;
  formData: {
    file: Blob | File;
    configSchema: string;
    description: string;
  };
};

export type UploadConfigRevisionMutationConfig = {
  onSuccess?: (data: ConfigRevisionResponse, variables: UploadConfigRevisionMutationRequest) => void;
  onError?: (error: unknown, variables: UploadConfigRevisionMutationRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: UploadConfigRevisionMutationRequest) => Promise<unknown> | void;
};

/**
 * Hook for uploading a new config revision through the management plane API.
 *
 * @param config.onSuccess - Called after successful config revision upload
 * @param config.onError - Called if upload fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for uploading config revision
 *
 * @example
 * ```tsx
 * const { mutate } = useUploadConfigRevisionMutation({
 *   onSuccess: (data) => console.log('Config revision uploaded:', data),
 *   onError: (error) => console.error('Config revision upload failed:', error)
 * });
 *
 * // Upload Config Revision
 * mutate({
 *   tenantId: 'tenant-uuid',
 *   configId: 'config-uuid',
 *   requestBody: {
 *     configSchema: 'updated config content',
 *     description: 'Revision description'
 *   }
 * });
 * ```
 */

export function useUploadConfigRevisionMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: UploadConfigRevisionMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isPending, isError, error } = useMutation<
    ConfigRevisionResponse,
    ApiError,
    UploadConfigRevisionMutationRequest
  >({
    mutationFn: ({ configId, formData }) => {
      return mgmtPlaneAPIClient.nodeConfigs.uploadConfigRevision({
        tenantId: tenantId!,
        configId,
        formData,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isPending, isError, error };
}
